import { useCallback, useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useInView } from 'react-intersection-observer';

import useElementSize from '@cartwhl/ui/hooks/useElementSize';

export interface UseCarouselOptions {
  /**
   * Total number of items in the carousel.
   * Required if `trackVisibleIndex` is true.
   */
  itemCount?: number;

  /**
   * Whether to snap to the nearest item after scrolling ends.
   * @default false
   */
  snapToItem?: boolean;

  /**
   * Whether to track the currently visible item index.
   * Requires `itemCount` to be provided.
   * @default false
   */
  trackVisibleIndex?: boolean;

  /**
   * Threshold for intersection observer to enable keyboard navigation.
   * @default 0.3
   */
  intersectionThreshold?: number;

  /**
   * Delay in milliseconds before snapping to nearest item after scroll ends.
   * Only used if `snapToItem` is true.
   * @default 150
   */
  snapDelay?: number;

  /**
   * Fallback gap value in pixels if CSS gap cannot be computed.
   * @default 16
   */
  fallbackGap?: number;
}

export interface UseCarouselReturn {
  /**
   * Ref to attach to the scrollable container element.
   */
  scrollRef: React.RefObject<HTMLDivElement | null>;

  /**
   * Ref to attach to the first carousel item (for width measurement).
   */
  firstItemRef: React.RefObject<HTMLDivElement | null>;

  /**
   * Ref to attach to the section element (for intersection observer).
   */
  intersectionRef: (node: Element | null) => void;

  /**
   * Whether the carousel can scroll to the previous item.
   */
  canScrollPrev: boolean;

  /**
   * Whether the carousel can scroll to the next item.
   */
  canScrollNext: boolean;

  /**
   * Currently visible item index (0-based).
   * Only available if `trackVisibleIndex` is true.
   */
  visibleIndex: number | undefined;

  /**
   * Width of a single carousel item in pixels.
   */
  itemWidth: number;

  /**
   * Gap between carousel items in pixels.
   */
  gap: number;

  /**
   * Function to scroll to the previous item.
   */
  scrollPrev: () => void;

  /**
   * Function to scroll to the next item.
   */
  scrollNext: () => void;
}

/**
 * Custom hook for managing horizontal carousel functionality.
 *
 * Provides scroll state management, keyboard navigation, and optional
 * features like snap-to-item and visible index tracking.
 *
 * @example
 * ```tsx
 * const {
 *   scrollRef,
 *   firstItemRef,
 *   intersectionRef,
 *   canScrollPrev,
 *   canScrollNext,
 *   scrollPrev,
 *   scrollNext,
 * } = useCarousel({
 *   itemCount: items.length,
 *   snapToItem: true,
 *   trackVisibleIndex: true,
 * });
 * ```
 */
export function useCarousel(
  options: UseCarouselOptions = {},
): UseCarouselReturn {
  const {
    itemCount,
    snapToItem = false,
    trackVisibleIndex = false,
    intersectionThreshold = 0.3,
    snapDelay = 150,
    fallbackGap = 16,
  } = options;

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const firstItemRef = useRef<HTMLDivElement | null>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);
  const [gap, setGap] = useState(0);
  const [visibleIndex, setVisibleIndex] = useState<number | undefined>(
    trackVisibleIndex ? 0 : undefined,
  );

  const { width: itemWidth } = useElementSize(firstItemRef);
  const { ref: intersectionRef, inView } = useInView({
    threshold: intersectionThreshold,
  });

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollPrev(scrollLeft > 0);
    setCanScrollNext(scrollLeft < scrollWidth - clientWidth - 1);

    // Calculate which item is currently visible
    if (trackVisibleIndex && itemWidth > 0 && gap >= 0 && itemCount) {
      const scrollPosition = scrollLeft;
      const itemWidthWithGap = itemWidth + gap;
      const newVisibleIndex = Math.round(scrollPosition / itemWidthWithGap);
      setVisibleIndex(Math.min(newVisibleIndex, itemCount - 1));
    }
  }, [itemWidth, gap, trackVisibleIndex, itemCount]);

  // Calculate gap from CSS
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    const computedStyle = window.getComputedStyle(scrollContainer);
    const gapValue = parseFloat(computedStyle.gap) || fallbackGap;
    setGap(gapValue);
  }, [fallbackGap]);

  // Set up scroll and resize listeners
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateScrollState();
    el.addEventListener('scroll', updateScrollState);
    window.addEventListener('resize', updateScrollState);

    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState]);

  // Update scroll state when item width changes (responsive breakpoints)
  useEffect(() => {
    updateScrollState();
  }, [itemWidth, updateScrollState]);

  const scrollPrev = useCallback(() => {
    if (itemWidth > 0 && gap >= 0) {
      scrollRef.current?.scrollBy({
        left: -(itemWidth + gap),
        behavior: 'smooth',
      });
    }
  }, [itemWidth, gap]);

  const scrollNext = useCallback(() => {
    if (itemWidth > 0 && gap >= 0) {
      scrollRef.current?.scrollBy({
        left: itemWidth + gap,
        behavior: 'smooth',
      });
    }
  }, [itemWidth, gap]);

  const snapToNearestItem = useCallback(() => {
    const el = scrollRef.current;
    if (!el || itemWidth <= 0 || gap < 0) return;

    const scrollLeft = el.scrollLeft;
    const itemWidthWithGap = itemWidth + gap;
    const nearestIndex = Math.round(scrollLeft / itemWidthWithGap);
    const targetScrollLeft = nearestIndex * itemWidthWithGap;

    el.scrollTo({
      left: targetScrollLeft,
      behavior: 'smooth',
    });
  }, [itemWidth, gap]);

  // Snap to nearest item after scroll ends (if enabled)
  useEffect(() => {
    if (!snapToItem) return;

    const el = scrollRef.current;
    if (!el) return;

    let scrollTimeout: ReturnType<typeof setTimeout>;

    const handleScroll = () => {
      clearTimeout(scrollTimeout);

      scrollTimeout = setTimeout(() => {
        snapToNearestItem();
      }, snapDelay);
    };

    el.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      el.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [snapToItem, snapDelay, snapToNearestItem]);

  // Arrow key navigation - only enabled when component is in view
  useHotkeys(
    'arrowleft',
    (e) => {
      e.preventDefault();
      if (canScrollPrev) {
        scrollPrev();
      }
    },
    {
      enabled: inView,
      enableOnFormTags: false,
    },
    [inView, canScrollPrev, scrollPrev],
  );

  useHotkeys(
    'arrowright',
    (e) => {
      e.preventDefault();
      if (canScrollNext) {
        scrollNext();
      }
    },
    {
      enabled: inView,
      enableOnFormTags: false,
    },
    [inView, canScrollNext, scrollNext],
  );

  return {
    scrollRef,
    firstItemRef,
    intersectionRef,
    canScrollPrev,
    canScrollNext,
    visibleIndex,
    itemWidth,
    gap,
    scrollPrev,
    scrollNext,
  };
}
